import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxComponent, NzCheckboxModule } from 'ng-zorro-antd/checkbox';

import { NzDividerModule } from 'ng-zorro-antd/divider';
import {
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzRadioGroupComponent, NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import {
  BehaviorSubject,
  finalize,
  from,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import { DRAWER_IDS } from '../../../../../core/constants/drawer-ids.constant';
import { ApiResponse } from '../../../../../core/models/api-response.models';
import {
  ApiResponses,
  BehaviourListModel,
  BehaviourLookUpModel,
  BehaviourSubListModel,
} from '../../../../../core/models/common.models';
import { DrawerService } from '../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../core/services/loader.service';
import { OrderByPipe } from '../../../../../shared/pipes/order-by.pipe';
import { CommonService } from '../../../../../shared/services/common.service';
import {
  InsightMode,
  BehaviorFrequency,
  CustomQuestionReportType,
} from '../../../insights.enums';
import {
  BehaviorDetailModel,
  BehaviorModel,
  caseBehaviorConsequenceDescriptions,
  caseBehaviorTypeDescriptions,
  CaseBehaviour,
  CaseDetailModel,
  EditBehaviorDrawerParams,
} from '../../../insights.interface';
import { BehaviorService } from './define-behavior.service';
import _ from 'lodash';
import { PrintCaseBehaviorComponent } from '../print-case-behavior/print-case-behavior.component';
import { OrganizationModel } from '../../../../organizations/organization.interface';
import { OrganizationService } from '../../../../organizations/organization.service';
import { CustomQuestionService } from '../../../../manage-settings/custom-question/custom-question.service';
import {
  behaviorGoals,
  QuestionItem,
} from '../../../../manage-settings/manage-settings.interface';
import { ManageSettingsService } from '../../../../manage-settings/manage-settings.service';
import behaviorGoalExamples from './goalsExample.json';
import { BipSummaryComponent } from '../bip-summary/bip-summary.component';
import { BIP_SUMMARY_DATA } from '../bip-summary/bip-summary-example';
import { AuditContextService } from '../../../../audit-logs/audit-context.service';
import { AuditFieldChange } from '../../../../audit-logs/audit-logs.interface';
import { FbaAssessmentComponent } from '../assessment-fba/fba-assessment/fba-assessment.component';
import { ReportLabelPipe } from '../../../../../core/pipes/report-label.pipe';
import { LabelKey } from '../../../../../core/models/report-labels.model';
import { ViewBipDrawerNavigationService } from '../shared/view-bip-drawer-navigation.service';
import { DrawerHeaderComponent } from '../../../../../components/drawer-header/drawer-header.component';
import { AuthService } from '../../../../../core/services/auth.service';
import { CmsRoleAccessService } from '../../../../../core/services/cms-role-access.service';
import { UserRole } from '../../../../activity-dashboard/activity-dashboard.enums';

@Component({
  selector: 'app-define-behavior',
  standalone: true,
  imports: [
    NzFormModule,
    SvgIconComponent,
    NzDividerModule,
    NzButtonModule,
    NzRadioGroupComponent,
    NzRadioModule,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzCheckboxModule,
    NzCheckboxComponent,
    NzInputModule,
    NzModalModule,
    CommonModule,
    NzSpinModule,
    ReactiveFormsModule,
    FormsModule,
    NzToolTipModule,
    OrderByPipe,
    BipSummaryComponent,
    DrawerHeaderComponent,
    ReportLabelPipe,
  ],
  templateUrl: './define-behavior.component.html',
  styleUrl: './define-behavior.component.scss',
})
export class DefineBehaviorComponent implements OnInit, OnDestroy {
  @ViewChild('drawerFBATitleTpl') drawerFBATitleTpl!: TemplateRef<void>;
  isVisible = false;
  isFunctionalEquivaluentModelVisible = false;
  isSettingEventModelVisible = false;
  isGoalExampleModelVisible = false;
  isPdfModalVisible = false;
  behaviorGoalExamples = behaviorGoalExamples;
  currentPage = 1;
  totalPages = 12;
  behvaiorSummaryData = BIP_SUMMARY_DATA;
  caseParams: EditBehaviorDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.ADD_BEHAVIOR,
    behaviorId: undefined,
  };
  isPrintLoading = false;
  behaviorGoal: behaviorGoals | null = null;
  private readonly fb = inject(FormBuilder);
  assessmentLabel: string = LabelKey.ASSESS_LABEL_OVERRIDE;
  private readonly formControls = {
    caseId: [null, [Validators.required]],
    behaviorCategoryId: [null, [Validators.required]],
    caseBehaviorTypeIds: [[], [Validators.required]],
    operationalDefinition: [
      '',
      [Validators.required, Validators.maxLength(4000)],
    ],
    behaviourFrequencyId: [null, [Validators.required]],
    behaviourFrequencyCount: [
      { value: null, disabled: true },
      [
        Validators.required,
        Validators.pattern('^[0-9]*$'),
        Validators.maxLength(3),
      ],
    ],
    isDurationRequired: [
      { value: null, disabled: true },
      [Validators.required],
    ],
    duration: [null, [Validators.pattern('^[0-9]*$'), Validators.maxLength(3)]],
    behaviourIntensityId: [null, [Validators.required]],
    goals: ['', [Validators.required, Validators.maxLength(4000)]],
    draft: [true],
    draftStep: [1],
    caseBehaviorAntecedentIds: [[], [Validators.required]],
    caseBehaviorConsequenceIds: [[], [Validators.required]],
    caseBehaviorEnvironmentIds: [[], [Validators.required]],
    caseBehaviorSettingEventIds: [[], [Validators.required]],
    caseBehaviorTypeDescriptions: this.fb.array([]),
    caseBehaviorConsequenceDescription: this.fb.array([]),
    caseBehaviorAntecedentDescription: this.fb.array([]),
    caseBehaviorCustomQuestionAnswer: this.fb.array([]),
  };

  form: FormGroup;
  behaviorCategoryList: BehaviourListModel[] = [];
  behaviourSubCategoryList: BehaviourSubListModel[] = [];
  behaviourFrequencyList: BehaviourListModel[] = [];
  behaviourIntensityList: BehaviourListModel[] = [];
  behaviourEnvironmentList: BehaviourListModel[] = [];
  behaviourSettingEventList: BehaviourListModel[] = [];
  behaviourAntecedentList: BehaviourListModel[] = [];
  behaviorConsequenceList: BehaviourListModel[] = [];
  customQuestionList: QuestionItem[] = [];
  loading$: Observable<boolean>;
  loading = false;
  saveLaterLoading = false;
  nextLoading = false;
  isContinueProcessing = false;
  private readonly nextLoadingSubject = new BehaviorSubject<boolean>(false);
  nextLoading$ = this.nextLoadingSubject.asObservable();
  behaviorDetail?: BehaviorDetailModel | null;
  private auditBaselineBehaviorDetail: BehaviorDetailModel | null = null;
  private lastModifiedFields: AuditFieldChange[] = [];
  pageTitles: { title: string; controls: string[]; tooltip?: string }[] = [
    {
      title: 'Category',
      controls: ['caseId', 'behaviorCategoryId'],
      tooltip: 'Select behavior category',
    },
    {
      title: 'Behavior Types',
      controls: ['caseBehaviorTypeIds', 'caseBehaviorTypeDescriptions'],
      tooltip: 'Choose behavior types and descriptions',
    },
    {
      title: 'Operational Definition',
      controls: ['operationalDefinition'],
      tooltip: 'Define operational behavior characteristics',
    },
    {
      title: 'Frequency',
      controls: [
        'behaviourFrequencyId',
        'behaviourFrequencyCount',
        'isDurationRequired',
        'duration',
      ],
      tooltip: 'Specify behavior frequency and duration',
    },
    {
      title: 'Intensity',
      controls: ['behaviourIntensityId'],
      tooltip: 'Indicate behavior intensity level',
    },
    {
      title: 'Environments',
      controls: ['caseBehaviorEnvironmentIds'],
      tooltip: 'Select relevant environments',
    },
    {
      title: 'Setting Events',
      controls: ['caseBehaviorSettingEventIds'],
      tooltip: 'Choose applicable setting events',
    },
    {
      title: 'Antecedents',
      controls: [
        'caseBehaviorAntecedentIds',
        'caseBehaviorAntecedentDescription',
      ],
      tooltip: 'Identify behavior antecedents',
    },
    {
      title: 'Consequences',
      controls: [
        'caseBehaviorConsequenceIds',
        'caseBehaviorConsequenceDescription',
      ],
      tooltip: 'Specify behavior consequences',
    },
    {
      title: 'Goals',
      controls: ['goals'],
      tooltip: 'Define behavior goals',
    },
    {
      title: 'Questions',
      controls: ['caseBehaviorCustomQuestionAnswer'],
      tooltip: 'Answer custom questions',
    },
    {
      title: 'Preview',
      controls: [],
      tooltip: 'Preview all entered information',
    },
  ];
  isPreviewPage = false;
  selectedCategory?: number;
  private readonly destroy$ = new Subject<void>();
  userRoles: string[] = [];
  roleEnum = UserRole;
  allowManageBehavior = true;

  constructor(
    private readonly drawerService: DrawerService,
    private readonly commonService: CommonService,
    private readonly loaderService: LoaderService,
    private readonly behaviorService: BehaviorService,
    private readonly cd: ChangeDetectorRef,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly injector: Injector,
    private readonly orgService: OrganizationService,
    private readonly customQuestionService: CustomQuestionService,
    private readonly manageSettingService: ManageSettingsService,
    private readonly auditContextService: AuditContextService,
    private readonly viewBipDrawerNavigationService: ViewBipDrawerNavigationService,
    private readonly authService: AuthService,
    private readonly cmsRoleAccessService: CmsRoleAccessService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.form = this.fb.group(this.formControls);
    this.setupDrawerSubscription();
  }

  ngOnInit() {
    this.authService.userRoles$
      .pipe(takeUntil(this.destroy$))
      .subscribe(roles => {
        if (roles) {
          this.userRoles = roles;
          this.allowManageBehavior =
            this.cmsRoleAccessService.computeAllowFromCmsRole(this.userRoles, [
              this.roleEnum.CMSManager,
              this.roleEnum.CMSDataEntry,
              this.roleEnum.CMSBrowser,
            ]);
        }
      });
    setTimeout(() => {
      this.getMasterData();
    }, 500);
    this.form.controls['behaviorCategoryId'].valueChanges.subscribe(
      (val: number) => {
        if (this.selectedCategory !== val) {
          this.selectedCategory = val;
          this.behaviourSubCategoryList = [];
          this.form.controls['caseBehaviorTypeIds'].setValue([]);
          (
            this.form.controls['caseBehaviorTypeDescriptions'] as FormArray
          ).clear();
          if (val) {
            this.getBehaviourSubTypes(val);
          }
        }
      }
    );
    this.form.controls['behaviourFrequencyId'].valueChanges.subscribe(
      (val: number) => {
        if (val) {
          this.form.controls['behaviourFrequencyCount'].enable();
          this.form.controls['isDurationRequired'].enable();
        } else {
          this.form.controls['behaviourFrequencyCount'].setValue(null);
          this.form.controls['isDurationRequired'].setValue(null);
          this.form.controls['behaviourFrequencyCount'].disable();
          this.form.controls['isDurationRequired'].disable();
        }
      }
    );
    this.form.controls['isDurationRequired'].valueChanges.subscribe(
      (val: boolean) => {
        if (val === true) {
          this.form.controls['duration'].enable();
          this.form.controls['duration'].setValidators([
            Validators.required,
            Validators.pattern('^[0-9]*$'),
            Validators.maxLength(3),
          ]);
          this.form.controls['duration'].updateValueAndValidity();
        } else {
          this.form.controls['duration'].setValue(null);
          this.form.controls['duration'].setValidators(null);
          this.form.controls['duration'].updateValueAndValidity();
        }
      }
    );
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setNextLoading(loading: boolean): void {
    this.nextLoading = loading;
    this.nextLoadingSubject.next(loading);
  }

  get isActionInProgress(): boolean {
    return this.nextLoading || this.saveLaterLoading;
  }

  getMasterData() {
    from(
      Promise.all([
        this.commonService.getBehaviourLookups().toPromise(),
        this.customQuestionService
          .getQuestionsByOrg(this.caseParams?.case?.student.organizationId)
          .toPromise(),
        this.manageSettingService
          .getbehaviorGoalsByOrgId(
            this.caseParams?.case?.student.organizationId
          )
          .toPromise(),
      ])
    )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (this.caseParams.mode === InsightMode.ADD_BEHAVIOR) {
            this.loaderService.hide();
          }
        })
      )
      .subscribe(
        ([response, queResponse, behvaiorGoalsRes]: [
          ApiResponse<BehaviourLookUpModel> | undefined,
          ApiResponses<QuestionItem> | undefined,
          ApiResponse<behaviorGoals> | undefined,
        ]) => {
          if (response?.result) {
            this.behaviorCategoryList = response.result.behaviorCategory;
            this.behaviourFrequencyList = response.result.behaviourFrequency;
            this.behaviourIntensityList = response.result.behaviourIntensity;
            this.behaviourEnvironmentList =
              response.result.behaviourEnvironment;
            this.behaviourSettingEventList =
              response.result.behaviourSettingEvent;
            this.behaviourAntecedentList = response.result.behaviourAntecedent;
            this.behaviorConsequenceList = response.result.behaviorConsequence;
          }
          this.customQuestionList = queResponse?.result
            ? _.sortBy(queResponse.result, 'sequenceNumber')
            : [];
          if (this.customQuestionList.length > 0) {
            this.customQuestionList.forEach(que => {
              const queRecord =
                this.behaviorDetail?.caseBehaviorCustomQuestionAnswer.find(
                  q => q.customQuestionId === que.id
                );

              const customQuestionGrp = this.fb.group({
                customQuestionId: [que.id],
                answer: [
                  queRecord ? queRecord.answer : '',
                  que.isRequired
                    ? [Validators.required, Validators.maxLength(6000)]
                    : [Validators.maxLength(6000)],
                ],
              });
              this.customQuestions.push(customQuestionGrp);
            });
          }
          this.behaviorGoal = behvaiorGoalsRes ? behvaiorGoalsRes.result : null;
          if (
            this.caseParams.mode === InsightMode.ADD_BEHAVIOR &&
            this.behaviorGoal?.statusId === 1
          ) {
            this.form.patchValue({
              goals: this.behaviorGoal?.goalTemplateText,
            });
          }

          this.cd.detectChanges();
        }
      );
  }

  async getBehaviourSubTypes(behaviourId: number) {
    this.commonService
      .getBehaviourSubTypes(behaviourId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponses<BehaviourSubListModel>) => {
          this.behaviourSubCategoryList = res.result;
          this.cd.detectChanges();
          this.loaderService.hide();
        },
        error: (error: Error) => {
          this.commonService.notify('error', error.message);
        },
      });
  }

  getSelectedBehaviorSubCategory() {
    const selected = this.form.controls['caseBehaviorTypeIds'].value;
    return (
      this.behaviourSubCategoryList.filter(res => selected.includes(res.id)) ||
      []
    );
  }

  getBehaviorFrequencyLabel(name: string) {
    return BehaviorFrequency[name as keyof typeof BehaviorFrequency];
  }

  getBehvaiorGoalLabel(categoryId: number) {
    const behvaiorCategory = this.behaviorCategoryList.find(
      cat => cat.id === categoryId
    );
    const behaviorGoalExamples = this.behaviorGoalExamples.behavioral_goals;
    if (behvaiorCategory) {
      const goalExample = behaviorGoalExamples.find(
        example =>
          example.goal.toLocaleLowerCase() ===
          behvaiorCategory.name.toLocaleLowerCase()
      );
      if (goalExample) {
        return goalExample.description;
      }
      return 'Add details';
    }
    return 'Add details';
  }

  private setupDrawerSubscription(): void {
    this.loaderService.show();
    this.drawerService
      .getParams<EditBehaviorDrawerParams>(DRAWER_IDS.DEFINE_BEHAVIOR)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          if (params.mode === InsightMode.ADD_BEHAVIOR) {
            this.form.get('caseId')?.setValue(this.caseParams.case?.id);
          } else {
            if (params.mode === InsightMode.EDIT_BEHAVIOR) {
              this.isPreviewPage = false;
            }
            this.getBehaviorById(this.caseParams.behaviorId);
          }
        }
      });
  }

  getBehaviorById(id: number | undefined) {
    if (id) {
      this.behaviorService
        .getBehaviorById(id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: async (response: ApiResponse<BehaviorDetailModel>) => {
            if (response.result) {
              this.behaviorDetail = response.result;
              this.auditBaselineBehaviorDetail = structuredClone(
                response.result
              );
              const caseBehaviorTypeIds = this.behaviorDetail[
                'caseBehaviorTypes'
              ].map(e => e.behaviorTypeId);
              const caseBehaviorEnvironmentIds = this.behaviorDetail[
                'caseBehaviorEnvironments'
              ].map(e => e.behaviorEnvironmentId);
              const caseBehaviorConsequenceIds = this.behaviorDetail[
                'caseBehaviorConsequences'
              ].map(e => e.behaviorConsequenceId);
              const caseBehaviorAntecedentIds = this.behaviorDetail[
                'caseBehaviorAntecedents'
              ].map(e => e.behaviorAntecedentId);
              const caseBehaviorSettingEventIds = this.behaviorDetail[
                'caseBehaviorSettingEvents'
              ].map(e => e.behaviorSettingEventId);
              const caseBehaviorTypeDescriptions = this.behaviorDetail[
                'caseBehaviorTypeDescriptions'
              ].map(e => {
                const caseBehaviorOther = this.fb.group({
                  behaviorTypeId: [e.behaviorTypeId],
                  description: [
                    '',
                    [Validators.required, Validators.maxLength(4000)],
                  ],
                });
                this.behaviorTypeDescription.push(caseBehaviorOther);
                return {
                  behaviorTypeId: e.behaviorTypeId,
                  description: e.description,
                };
              });
              const caseBehaviorConsequenceDescription = this.behaviorDetail[
                'caseBehaviorConsequenceDescriptions'
              ].map(e => {
                const consequenceOther = this.fb.group({
                  behaviorConsequenceId: [e.behaviorConsequenceId],
                  description: [
                    '',
                    [Validators.required, Validators.maxLength(4000)],
                  ],
                });
                this.consequenceDescription.push(consequenceOther);
                return {
                  behaviorConsequenceId: e.behaviorConsequenceId,
                  description: e.description,
                };
              });
              const caseBehaviorAntecedentDescription =
                this.behaviorDetail['caseBehaviorAntecedentDescriptions']?.map(
                  e => {
                    const antecedentOther = this.fb.group({
                      behaviorAntecedentId: [e.behaviorAntecedentId],
                      description: [
                        '',
                        [Validators.required, Validators.maxLength(4000)],
                      ],
                    });
                    this.antecedentDescription.push(antecedentOther);
                    return {
                      behaviorAntecedentId: e.behaviorAntecedentId,
                      description: e.description,
                    };
                  }
                ) || [];
              const formData = {
                ...this.behaviorDetail,
                caseBehaviorTypeIds,
                caseBehaviorEnvironmentIds,
                caseBehaviorAntecedentIds,
                caseBehaviorSettingEventIds,
                caseBehaviorConsequenceIds,
                caseBehaviorTypeDescriptions,
                caseBehaviorConsequenceDescription,
                caseBehaviorAntecedentDescription,
              };

              this.getBehaviourSubTypes(this.behaviorDetail.behaviorCategoryId);
              this.selectedCategory = this.behaviorDetail.behaviorCategoryId;

              this.form.patchValue(formData);

              this.form
                .get('isDurationRequired')
                ?.setValue(!!this.behaviorDetail.duration);
              this.form.markAllAsTouched();
              this.currentPage =
                this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
                  ? 12
                  : this.behaviorDetail.draftStep || 1;
              this.managePreview();
              if (
                this.caseParams.mode === InsightMode.VIEW_BEHAVIOR ||
                this.currentPage === this.totalPages
              ) {
                this.isPreviewPage = true;
              } else {
                this.form.enable();
                this.isPreviewPage = false;
              }
            }
          },
          error: error => console.error(error),
        });
    }
  }
  onCheckboxChange(ctrl: string, checked: boolean, value: number): void {
    this.form.get(ctrl)?.markAsTouched();
    const selectedOptions = this.form.get(ctrl)?.value || [];
    if (checked) {
      selectedOptions.push(value);
    } else {
      const index = selectedOptions.indexOf(value);
      if (index > -1) {
        selectedOptions.splice(index, 1);
      }
    }
    this.form.get(ctrl)?.setValue(selectedOptions);
  }

  get behaviorTypeDescription(): FormArray {
    return this.form.controls['caseBehaviorTypeDescriptions'] as FormArray;
  }

  get consequenceDescription(): FormArray {
    return this.form.controls[
      'caseBehaviorConsequenceDescription'
    ] as FormArray;
  }

  get antecedentDescription(): FormArray {
    return this.form.controls['caseBehaviorAntecedentDescription'] as FormArray;
  }

  get customQuestions(): FormArray {
    return this.form.controls['caseBehaviorCustomQuestionAnswer'] as FormArray;
  }

  get customQuestionsControls(): FormGroup[] {
    return this.customQuestions?.controls as FormGroup[];
  }

  get behaviorTypeDescriptionControls(): FormGroup[] {
    return this.behaviorTypeDescription?.controls as FormGroup[];
  }

  get consequenceDescriptionControls(): FormGroup[] {
    return this.consequenceDescription?.controls as FormGroup[];
  }
  get isAssessmentCompleted(): boolean {
    const matchedBehavior = this.caseParams.case?.caseBehaviors.find(
      behavior => behavior.id === this.caseParams?.behaviorId
    );
    return matchedBehavior
      ? matchedBehavior.behaviorFunction.length === 0 ||
          matchedBehavior.behaviorAssessmentGraph.length === 0
      : true;
  }

  get antecedentDescriptionControls(): FormGroup[] {
    return this.antecedentDescription?.controls as FormGroup[];
  }

  getBehaviorTypeDescriptionTitle(id: number): string {
    const item = this.behaviourSubCategoryList.find(c => c.id === id);
    return item ? item.name : '';
  }

  getBehaviorConsequenceTitle(id: number): string {
    const item = this.behaviorConsequenceList.find(c => c.id === id);
    return item ? item.name : '';
  }

  getBehaviorAntecedentTitle(id: number): string {
    const item = this.behaviourAntecedentList.find(c => c.id === id);
    return item ? item.name : '';
  }

  onBehaviorTypeChange(checked: boolean, category: BehaviourSubListModel) {
    if (checked && category.isOther) {
      const caseBehaviorOther = this.fb.group({
        behaviorTypeId: [category.id],
        description: ['', [Validators.required, Validators.maxLength(4000)]],
      });
      this.behaviorTypeDescription.push(caseBehaviorOther);
    } else if (category.isOther && !checked) {
      const index = this.behaviorTypeDescription.value.findIndex(
        (v: caseBehaviorTypeDescriptions) => v.behaviorTypeId === category.id
      );
      if (index !== -1) {
        this.behaviorTypeDescription.removeAt(index);
      }
    }
  }

  onControlInputChange(event: Event, control: AbstractControl | null): void {
    const input = event.target as HTMLTextAreaElement;
    const value = input.value;
    if (/^\s+$/.test(value)) {
      input.value = '';
      control?.setValue('');
    }
  }

  onConsequenceChange(checked: boolean, category: BehaviourListModel) {
    if (checked && category.isOther) {
      const consequenceOther = this.fb.group({
        behaviorConsequenceId: [category.id],
        description: ['', [Validators.required, Validators.maxLength(4000)]],
      });
      this.consequenceDescription.push(consequenceOther);
    } else if (category.isOther && !checked) {
      const index = this.consequenceDescription.value.findIndex(
        (v: caseBehaviorConsequenceDescriptions) =>
          v.behaviorConsequenceId === category.id
      );
      if (index !== -1) {
        this.consequenceDescription.removeAt(index);
      }
    }
  }

  onAntecedentChange(checked: boolean, category: BehaviourListModel) {
    if (checked && (category.name === 'Other' || category.id === 20)) {
      const antecedentOther = this.fb.group({
        behaviorAntecedentId: [category.id],
        description: ['', [Validators.required, Validators.maxLength(4000)]],
      });
      this.antecedentDescription.push(antecedentOther);
    } else if (!checked && (category.name === 'Other' || category.id === 20)) {
      const index = this.antecedentDescription.value.findIndex(
        (v: { behaviorAntecedentId: number }) =>
          v.behaviorAntecedentId === category.id
      );
      if (index !== -1) {
        this.antecedentDescription.removeAt(index);
      }
    }
  }

  currentControls() {
    const controlNames = this.pageTitles[this.currentPage - 1].controls;
    return controlNames.map(control => this.form.get(control));
  }

  showWarningModal(): void {
    this.isVisible = true;
  }
  showBehaviorDefinedModal(): void {
    this.isVisible = true;
  }
  handleOk(): void {
    this.isVisible = false;
  }
  handleCancel(): void {
    this.isVisible = false;
  }
  handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.showWarningModal();
    }
  }

  onSave() {
    if (this.isActionInProgress) {
      return;
    }
    if (!this.checkCurrentPageValidations()) {
      return;
    }
    this.setNextLoading(true);
    this.managePreview(true);
    this.saveChanges();
    this.isPreviewPage = true;
    this.currentPage = this.totalPages;
    // Reset loading if saveChanges didn't execute (draft is false)
    if (!this.form.get('draft')?.value) {
      this.setNextLoading(false);
      this.cd.detectChanges();
    }
  }

  onSaveAndNext() {
    if (this.isActionInProgress) {
      return;
    }
    if (!this.checkCurrentPageValidations()) {
      return;
    }
    this.setNextLoading(true);
    this.saveChanges();
    if (this.currentPage < this.totalPages) {
      this.currentPage = this.currentPage + 1;
    }
    if (this.currentPage === this.totalPages) {
      this.isPreviewPage = true;
      this.managePreview(true);
    }
    if (!this.form.get('draft')?.value) {
      this.setNextLoading(false);
      this.cd.detectChanges();
    }
  }

  onPrevious() {
    this.currentPage = this.currentPage - 1;
    if (this.currentPage < this.totalPages) {
      this.isPreviewPage = false;
      this.form.enable();
    }
  }

  checkCurrentPageValidations() {
    return this.currentControls().every(control => control?.valid);
  }
  managePreview(isUpdateDetail = false) {
    this.form.disable();
    this.isPreviewPage = true;
    if (isUpdateDetail) {
      const formData = this.form.getRawValue();

      this.behaviorDetail = {
        ...formData,
        behaviorCategoryId: formData.behaviorCategoryId,
        behaviorCategoryName: _.get(
          _.find(this.behaviorCategoryList, {
            id: formData.behaviorCategoryId,
          }),
          'name',
          ''
        ),
        operationalDefinition: formData.operationalDefinition,
        behaviourFrequencyId: formData.behaviourFrequencyId,
        behaviourFrequencyName: _.get(
          _.find(this.behaviourFrequencyList, {
            id: formData.behaviourFrequencyId,
          }),
          'name',
          ''
        ),
        behaviourFrequencyCount: formData.behaviourFrequencyCount,
        duration: formData.duration,
        behaviourIntensityId: formData.behaviourIntensityId,
        behaviourIntensityName: _.get(
          _.find(this.behaviourIntensityList, {
            id: formData.behaviourIntensityId,
          }),
          'name',
          ''
        ),
        goals: formData.goals,
        caseBehaviorTypes: _.filter(this.behaviourSubCategoryList, item =>
          _.includes(formData.caseBehaviorTypeIds, item.id)
        ).map(item => ({
          behaviorTypeId: item.id,
          behaviorTypeName: item.name,
        })),
        caseBehaviorAntecedents: _.filter(this.behaviourAntecedentList, item =>
          _.includes(formData.caseBehaviorAntecedentIds, item.id)
        ).map(item => ({
          behaviorAntecedentId: item.id,
          behaviorAntecedentName: item.name,
        })),
        caseBehaviorConsequences: _.filter(this.behaviorConsequenceList, item =>
          _.includes(formData.caseBehaviorConsequenceIds, item.id)
        ).map(item => ({
          behaviorConsequenceId: item.id,
          behaviorConsequenceName: item.name,
        })),
        caseBehaviorEnvironments: _.filter(
          this.behaviourEnvironmentList,
          item => _.includes(formData.caseBehaviorEnvironmentIds, item.id)
        ).map(item => ({
          behaviorEnvironmentId: item.id,
          behaviorEnvironmentName: item.name,
        })),
        caseBehaviorSettingEvents: _.filter(
          this.behaviourSettingEventList,
          item => _.includes(formData.caseBehaviorSettingEventIds, item.id)
        ).map(item => ({
          behaviorSettingEventId: item.id,
          behaviorSettingEventName: item.name,
        })),
        caseBehaviorTypeDescriptions: formData.caseBehaviorTypeDescriptions.map(
          (desc: caseBehaviorTypeDescriptions) => ({
            behaviorTypeId: desc.behaviorTypeId,
            description: desc.description,
          })
        ),
        caseBehaviorConsequenceDescriptions:
          formData.caseBehaviorConsequenceDescription.map(
            (desc: caseBehaviorConsequenceDescriptions) => ({
              behaviorConsequenceId: desc.behaviorConsequenceId,
              description: desc.description,
            })
          ),
        caseBehaviorAntecedentDescriptions:
          formData.caseBehaviorAntecedentDescription.map(
            (desc: { behaviorAntecedentId: number; description: string }) => ({
              behaviorAntecedentId: desc.behaviorAntecedentId,
              description: desc.description,
            })
          ),
        caseBehaviorCustomQuestionAnswer:
          formData.caseBehaviorCustomQuestionAnswer.map(
            (ans: { customQuestionId: number; answer: string }) => ({
              customQuestionId: ans.customQuestionId,
              customQuestion:
                this.customQuestionList.find(q => q.id === ans.customQuestionId)
                  ?.customQuestion || '',
              answer: ans.answer,
            })
          ),
      } as BehaviorDetailModel;
    }
  }

  redirectToStep(event: number) {
    if (!this.allowManageBehavior) {
      return;
    }
    this.currentPage = event;
    this.caseParams.mode = InsightMode.EDIT_BEHAVIOR;
    this.isPreviewPage = false;
    this.form.enable();
  }

  saveChanges() {
    if (this.form.get('draft')?.value) {
      this.form.get('draftStep')?.setValue(this.currentPage);
      const stepFields = this.pageTitles[this.currentPage - 1].controls;
      const _modifiedFields = this.getModifiedFields(stepFields);
      const payloadData = this.buildPayload(_modifiedFields);

      if (this.isEditMode()) {
        this.performEditBehavior(payloadData);
      } else {
        this.performCreateBehavior(payloadData);
      }
    }
  }

  private isEditMode(): boolean {
    return (
      this.caseParams.mode === InsightMode.EDIT_BEHAVIOR ||
      this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
    );
  }

  private getModifiedFields(stepFields: string[]): AuditFieldChange[] {
    if (!this.isTrackableMode()) {
      return [];
    }

    const modifiedFields = this.buildBehaviorModifiedFields(
      this.form.getRawValue() as Record<string, unknown>,
      stepFields
    );
    if (modifiedFields.length > 0) {
      this.lastModifiedFields = modifiedFields;
    }

    return modifiedFields;
  }

  private isTrackableMode(): boolean {
    return (
      this.caseParams.mode === InsightMode.ADD_BEHAVIOR ||
      this.caseParams.mode === InsightMode.EDIT_BEHAVIOR ||
      this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
    );
  }

  private buildPayload(
    modifiedFields: AuditFieldChange[]
  ): Record<string, unknown> {
    const basePayload = this.form.getRawValue();

    if (modifiedFields.length === 0) {
      return basePayload;
    }

    return {
      ...basePayload,
      _modifiedFields: modifiedFields,
    };
  }

  private performEditBehavior(payloadData: Record<string, unknown>): void {
    this.behaviorService
      .editBehavior(
        payloadData as unknown as BehaviorModel,
        Number(this?.caseParams?.behaviorId)
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.resetLoadingStates())
      )
      .subscribe(() => {
        this.setNextLoading(false);
        this.cd.detectChanges();
      });
  }

  private performCreateBehavior(payloadData: Record<string, unknown>): void {
    this.behaviorService
      .createBehavior(payloadData as unknown as BehaviorModel)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.resetLoadingStates())
      )
      .subscribe(res => {
        this.caseParams.behaviorId = res?.result.id;
        this.caseParams.mode = InsightMode.EDIT_BEHAVIOR;
        this.setNextLoading(false);
        this.cd.detectChanges();
      });
  }

  private resetLoadingStates(): void {
    this.setNextLoading(false);
    this.loading = false;
    this.saveLaterLoading = false;
    this.cd.detectChanges();
  }

  checkValidation(control: AbstractControl | null): boolean {
    if (!control) return false;
    if (control.disabled) {
      const errors = control.validator?.(control);
      return errors?.['required'];
    }
    return control.valid;
  }

  checkFormGroupValidity(): boolean {
    let isValid = true;

    Object.keys(this.form.controls).forEach(controlName => {
      const control = this.form.get(controlName);
      if (control?.disabled) {
        control.enable({ emitEvent: false });
      }
    });

    isValid = this.form.valid;

    Object.keys(this.form.controls).forEach(controlName => {
      const control = this.form.get(controlName);
      if (control?.enabled) {
        control.disable({ emitEvent: false });
      }
    });

    return isValid;
  }

  saveForLater() {
    if (this.isActionInProgress) {
      return;
    }
    this.form.get('draftStep')?.setValue(this.currentPage);
    this.form.get('draft')?.setValue(true);
    this.saveLaterLoading = true;
    if (
      this.form.value.draft &&
      !!this.form.value.behaviorCategoryId &&
      !!this.form.value.caseId
    ) {
      this.saveBehavior(this.form.value, true);
    }
  }

  onSubmit() {
    if (this.isActionInProgress) {
      return;
    }
    this.form.get('draftStep')?.setValue(this.currentPage);
    this.form.get('draft')?.setValue(false);
    if (!this.checkFormGroupValidity()) {
      return;
    }
    this.loading = true;
    this.saveBehavior(this.form.getRawValue(), false);
  }
  onContinueToAssessment(): void {
    this.form.get('draftStep')?.setValue(this.currentPage);
    this.form.get('draft')?.setValue(false);
    if (!this.checkFormGroupValidity()) {
      return;
    }
    this.isContinueProcessing = true;
    this.loading = true;
    this.saveBehavior(this.form.getRawValue(), false);
  }
  private buildBehaviorModifiedFields(
    formData: Record<string, unknown>,
    stepFields?: string[]
  ): AuditFieldChange[] {
    const isCreateMode = this.caseParams.mode === InsightMode.ADD_BEHAVIOR;
    const baselineBehaviorDetail = this.auditBaselineBehaviorDetail;
    const hasPersistedBaseline = !isCreateMode && !!baselineBehaviorDetail;
    const labelMap: Record<string, string> = {
      behaviorCategory: 'Behavior Category',
      operationalDefinition: 'Operational Definition',
      behaviorTypes: 'Behavior Types',
      caseBehaviorTypeDescriptions: 'Behavior Type Descriptions',
      behaviorFrequency: 'Frequency',
      behaviourFrequencyCount: 'Frequency Count',
      isDurationRequired: 'Duration Required',
      duration: 'Duration',
      behaviorIntensity: 'Intensity',
      goals: 'Goals',
      draft: 'Draft Status',
      draftStep: 'Draft Step',
      behaviorAntecedents: 'Antecedents',
      caseBehaviorAntecedentDescriptions: 'Antecedent Descriptions',
      behaviorConsequences: 'Consequences',
      caseBehaviorConsequenceDescriptions: 'Consequence Descriptions',
      behaviorEnvironments: 'Environments',
      behaviorSettingEvents: 'Setting Events',
      customQuestions: 'Custom Questions',
      caseBehaviorCustomQuestionAnswer: 'Custom Question Answers',
    };

    const sensitiveFields: string[] = [];
    const idFields = [
      'behaviorCategoryId',
      'caseBehaviorTypeIds',
      'behaviourFrequencyId',
      'behaviourIntensityId',
      'caseBehaviorAntecedentIds',
      'caseBehaviorConsequenceIds',
      'caseBehaviorEnvironmentIds',
      'caseBehaviorSettingEventIds',
      'caseBehaviorCustomQuestionAnswer',
    ];
    const idDerivedFieldMap: Record<string, string> = {
      behaviorCategoryId: 'behaviorCategory',
      caseBehaviorTypeIds: 'behaviorTypes',
      behaviourFrequencyId: 'behaviorFrequency',
      behaviourIntensityId: 'behaviorIntensity',
      caseBehaviorAntecedentIds: 'behaviorAntecedents',
      caseBehaviorConsequenceIds: 'behaviorConsequences',
      caseBehaviorEnvironmentIds: 'behaviorEnvironments',
      caseBehaviorSettingEventIds: 'behaviorSettingEvents',
      caseBehaviorCustomQuestionAnswer: 'customQuestions',
    };
    const stepFieldMap: Record<string, string> = {
      ...idDerivedFieldMap,
      caseBehaviorAntecedentDescription: 'caseBehaviorAntecedentDescriptions',
      caseBehaviorConsequenceDescription: 'caseBehaviorConsequenceDescriptions',
    };
    const excludeFields = [
      'id',
      'caseId',
      ...idFields,
      'caseBehaviorConsequenceDescription',
      'caseBehaviorAntecedentDescription',
    ];

    const allOriginal: Record<string, unknown> = hasPersistedBaseline
      ? {
          behaviorCategoryId:
            baselineBehaviorDetail?.behaviorCategoryId || null,
          operationalDefinition:
            baselineBehaviorDetail?.operationalDefinition || '',
          caseBehaviorTypeIds: this.normalizeIdArray(
            baselineBehaviorDetail?.caseBehaviorTypes?.map(
              (x: { behaviorTypeId: number }) => x.behaviorTypeId
            ) || []
          ),
          caseBehaviorTypeDescriptions: this.normalizeDescriptionArray(
            baselineBehaviorDetail?.caseBehaviorTypeDescriptions || []
          ),
          behaviourFrequencyId:
            baselineBehaviorDetail?.behaviourFrequencyId ?? null,
          behaviourFrequencyCount:
            baselineBehaviorDetail?.behaviourFrequencyCount ?? null,
          isDurationRequired: baselineBehaviorDetail?.duration
            ? !!baselineBehaviorDetail.duration
            : null,
          duration: baselineBehaviorDetail?.duration ?? null,
          behaviourIntensityId:
            baselineBehaviorDetail?.behaviourIntensityId ?? null,
          goals: baselineBehaviorDetail?.goals || '',
          draft: baselineBehaviorDetail?.draft ?? true,
          draftStep: baselineBehaviorDetail?.draftStep || 1,
          caseBehaviorAntecedentIds: this.normalizeIdArray(
            baselineBehaviorDetail?.caseBehaviorAntecedents?.map(
              (x: { behaviorAntecedentId: number }) => x.behaviorAntecedentId
            ) || []
          ),
          caseBehaviorAntecedentDescriptions: this.normalizeDescriptionArray(
            baselineBehaviorDetail?.caseBehaviorAntecedentDescriptions || []
          ),
          caseBehaviorConsequenceIds: this.normalizeIdArray(
            baselineBehaviorDetail?.caseBehaviorConsequences?.map(
              (x: { behaviorConsequenceId: number }) => x.behaviorConsequenceId
            ) || []
          ),
          caseBehaviorConsequenceDescriptions: this.normalizeDescriptionArray(
            baselineBehaviorDetail?.caseBehaviorConsequenceDescriptions || []
          ),
          caseBehaviorEnvironmentIds: this.normalizeIdArray(
            baselineBehaviorDetail?.caseBehaviorEnvironments?.map(
              (x: { behaviorEnvironmentId: number }) => x.behaviorEnvironmentId
            ) || []
          ),
          caseBehaviorSettingEventIds: this.normalizeIdArray(
            baselineBehaviorDetail?.caseBehaviorSettingEvents?.map(
              (x: { behaviorSettingEventId: number }) =>
                x.behaviorSettingEventId
            ) || []
          ),
          caseBehaviorCustomQuestionAnswer:
            baselineBehaviorDetail?.caseBehaviorCustomQuestionAnswer || [],
        }
      : {};

    const allFormData: Record<string, unknown> = {
      ...formData,
      caseBehaviorTypeIds: this.normalizeIdArray(
        formData['caseBehaviorTypeIds']
      ),
      caseBehaviorTypeDescriptions: this.normalizeDescriptionArray(
        formData['caseBehaviorTypeDescriptions']
      ),
      caseBehaviorAntecedentIds: this.normalizeIdArray(
        formData['caseBehaviorAntecedentIds']
      ),
      caseBehaviorAntecedentDescriptions: this.normalizeDescriptionArray(
        formData['caseBehaviorAntecedentDescription']
      ),
      caseBehaviorConsequenceIds: this.normalizeIdArray(
        formData['caseBehaviorConsequenceIds']
      ),
      caseBehaviorConsequenceDescriptions: this.normalizeDescriptionArray(
        formData['caseBehaviorConsequenceDescription']
      ),
      caseBehaviorEnvironmentIds: this.normalizeIdArray(
        formData['caseBehaviorEnvironmentIds']
      ),
      caseBehaviorSettingEventIds: this.normalizeIdArray(
        formData['caseBehaviorSettingEventIds']
      ),
      caseBehaviorCustomQuestionAnswer:
        formData['caseBehaviorCustomQuestionAnswer'] || [],
      isDurationRequired: !!formData['isDurationRequired'],
    };

    Object.entries(idDerivedFieldMap).forEach(([idField, derivedField]) => {
      if (!isCreateMode) {
        allOriginal[derivedField] = this.resolveAuditFieldName(
          idField,
          allOriginal[idField]
        );
      }
      allFormData[derivedField] = this.resolveAuditFieldName(
        idField,
        allFormData[idField]
      );
    });

    if (!isCreateMode) {
      allOriginal['customQuestions'] = this.normalizeCustomQuestionAnswers(
        allOriginal['caseBehaviorCustomQuestionAnswer']
      );
    }
    allFormData['customQuestions'] = this.normalizeCustomQuestionAnswers(
      allFormData['caseBehaviorCustomQuestionAnswer']
    );

    let original: Record<string, unknown> = {};
    let filteredFormData: Record<string, unknown> = {};

    if (stepFields && stepFields.length > 0) {
      const normalizedStepFields = Array.from(
        new Set(stepFields.map(field => stepFieldMap[field] || field))
      );

      normalizedStepFields.forEach(field => {
        original[field] = allOriginal[field];
        filteredFormData[field] = allFormData[field];
      });
    } else {
      original = allOriginal;
      filteredFormData = allFormData;
    }

    const rawChanges = this.auditContextService.buildFieldChanges(
      original,
      filteredFormData,
      { sensitiveFields, labelMap, excludeFields }
    );

    const normalizedChanges = rawChanges
      .filter(change => change.oldValue !== change.newValue)
      .filter(change => {
        if (!this.isDescriptionField(change.field || '')) {
          return true;
        }

        return !(
          this.isEmptyAuditValue(change.oldValue) &&
          this.isEmptyAuditValue(change.newValue)
        );
      })
      .map(change => ({
        ...change,
        oldValue: this.convertBooleanToYesNo(change.oldValue),
        newValue: this.convertBooleanToYesNo(change.newValue),
      }));

    const shouldTrackCustomQuestions =
      !stepFields || stepFields.includes('caseBehaviorCustomQuestionAnswer');

    if (shouldTrackCustomQuestions) {
      const oldCustomQuestions = this.toAuditAnswerString(
        original['customQuestions']
      );
      const newCustomQuestions = this.toAuditAnswerString(
        filteredFormData['customQuestions']
      );

      const hasCustomQuestionsChange = normalizedChanges.some(
        change => change.field === 'customQuestions'
      );

      if (
        !hasCustomQuestionsChange &&
        oldCustomQuestions !== newCustomQuestions
      ) {
        normalizedChanges.push({
          field: 'customQuestions',
          fieldLabel: labelMap['customQuestions'] || 'customQuestions',
          oldValue: oldCustomQuestions,
          newValue: newCustomQuestions,
          isSensitive: false,
        });
      }
    }

    return normalizedChanges;
  }

  private convertBooleanToYesNo(value: unknown): string {
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase().trim();
      if (lowerValue === 'true') {
        return 'Yes';
      }
      if (lowerValue === 'false') {
        return 'No';
      }
      return value;
    }
    if (typeof value === 'number') {
      return String(value);
    }
    return '';
  }

  private resolveAuditFieldName(field: string, value: unknown): unknown {
    const ids = this.normalizeIdArray(value);

    const mapIdsToNames = (source: { id: number; name?: string }[]): string[] =>
      ids
        .map(id => source.find(item => item.id === Number(id))?.name || `${id}`)
        .filter(name => !!name);

    switch (field) {
      case 'behaviorCategoryId':
        return (
          this.behaviorCategoryList.find(q => q.id === value)?.name || value
        );
      case 'caseBehaviorTypeIds':
        return mapIdsToNames(this.behaviourSubCategoryList);
      case 'caseBehaviorEnvironmentIds':
        return mapIdsToNames(this.behaviourEnvironmentList);
      case 'caseBehaviorSettingEventIds':
        return mapIdsToNames(this.behaviourSettingEventList);
      case 'caseBehaviorAntecedentIds':
        return mapIdsToNames(this.behaviourAntecedentList);
      case 'caseBehaviorConsequenceIds':
        return mapIdsToNames(this.behaviorConsequenceList);
      case 'behaviourFrequencyId':
        return (
          this.behaviourFrequencyList.find(q => q.id === value)?.name || value
        );
      case 'behaviourIntensityId':
        return (
          this.behaviourIntensityList.find(q => q.id === value)?.name || value
        );
      default:
        return value;
    }
  }

  private normalizeIdArray(value: unknown): (number | string)[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map(item => {
        if (item === null || item === undefined) {
          return null;
        }

        if (typeof item === 'number' || typeof item === 'string') {
          return item;
        }

        if (typeof item === 'object') {
          const typedItem = item as Record<string, unknown>;
          return (
            typedItem['id'] ??
            typedItem['behaviorTypeId'] ??
            typedItem['behaviorAntecedentId'] ??
            typedItem['behaviorConsequenceId'] ??
            typedItem['behaviorEnvironmentId'] ??
            typedItem['behaviorSettingEventId'] ??
            null
          );
        }

        return null;
      })
      .filter((item): item is number | string => item !== null)
      .sort((a, b) => `${a}`.localeCompare(`${b}`));
  }

  private normalizeDescriptionArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map(item => {
        if (typeof item === 'string') {
          return item.trim();
        }

        if (item && typeof item === 'object') {
          const description = (item as Record<string, unknown>)['description'];
          return typeof description === 'string' ? description.trim() : '';
        }

        return '';
      })
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));
  }

  private normalizeCustomQuestionAnswers(
    value: unknown
  ): Record<string, string> {
    if (!Array.isArray(value)) {
      return {};
    }

    const normalized: Record<string, string> = {};

    value.forEach(item => {
      if (!item || typeof item !== 'object') {
        return;
      }

      const typedItem = item as Record<string, unknown>;
      const questionId = typedItem['customQuestionId'];
      const answer = typedItem['answer'];
      const questionFromLookup = this.customQuestionList.find(
        q => q.id === Number(questionId)
      )?.customQuestion;
      const questionFromPayload =
        (typedItem['customQuestion'] as string | undefined) ||
        (typedItem['question'] as string | undefined) ||
        (typedItem['questionText'] as string | undefined);
      const question = questionFromLookup || questionFromPayload;
      const questionIdText =
        typeof questionId === 'string' || typeof questionId === 'number'
          ? String(questionId)
          : '';
      const fallbackQuestionKey = questionIdText
        ? `Question ID ${questionIdText}`
        : '';
      const questionKey = question || fallbackQuestionKey;

      if (!questionKey) {
        return;
      }

      normalized[questionKey] = this.toAuditAnswerString(answer);
    });

    return Object.keys(normalized)
      .sort((a, b) => a.localeCompare(b))
      .reduce<Record<string, string>>((acc, key) => {
        acc[key] = normalized[key];
        return acc;
      }, {});
  }

  private isDescriptionField(field: string): boolean {
    return [
      'caseBehaviorTypeDescriptions',
      'caseBehaviorAntecedentDescriptions',
      'caseBehaviorConsequenceDescriptions',
    ].includes(field);
  }

  private isEmptyAuditValue(value: unknown): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value !== 'string') {
      return false;
    }

    const normalized = value.trim();
    return (
      normalized === '' ||
      normalized === '[]' ||
      normalized === '{}' ||
      normalized === 'null'
    );
  }

  private toAuditAnswerString(value: unknown): string {
    if (value === undefined || value === null) {
      return '';
    }

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return String(value);
    }

    return JSON.stringify(value);
  }
  saveBehavior(formData: BehaviorModel, isDraft: boolean) {
    const stepFields = isDraft
      ? this.pageTitles[this.currentPage - 1].controls
      : undefined;

    const _modifiedFields =
      this.caseParams.mode === InsightMode.EDIT_BEHAVIOR ||
      this.caseParams.mode === InsightMode.VIEW_BEHAVIOR ||
      this.caseParams.mode === InsightMode.ADD_BEHAVIOR
        ? this.buildBehaviorModifiedFields(
            formData as unknown as Record<string, unknown>,
            stepFields
          )
        : [];

    let modifiedFieldsToSend: AuditFieldChange[] = [];
    if (_modifiedFields.length > 0) {
      modifiedFieldsToSend = _modifiedFields;
    } else if (!isDraft && this.lastModifiedFields.length > 0) {
      modifiedFieldsToSend = this.lastModifiedFields;
    }

    if (_modifiedFields.length > 0) {
      this.lastModifiedFields = _modifiedFields;
    }

    const payloadData = {
      ...formData,
      ...(modifiedFieldsToSend.length > 0
        ? { _modifiedFields: modifiedFieldsToSend }
        : {}),
    };

    const operation$ =
      this.caseParams.mode === InsightMode.EDIT_BEHAVIOR ||
      this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
        ? this.behaviorService.editBehavior(
            payloadData,
            Number(this?.caseParams?.behaviorId)
          )
        : this.behaviorService.createBehavior(payloadData);

    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.saveLaterLoading = false;
          this.setNextLoading(false);
        })
      )
      .subscribe({
        next: res => {
          this.handleSubmissionSuccess(isDraft, res.result);
          if (!isDraft) {
            this.lastModifiedFields = [];
          }
          this.saveLaterLoading = false;
        },
        error: (error: Error) => {
          this.handleError(error);
          this.saveLaterLoading = false;
        },
      });
  }

  private handleSubmissionSuccess(
    isDraft: boolean,
    result: BehaviorModel
  ): void {
    this.showNotification(isDraft);
    this.closeDrawer(true, result);
  }

  private showNotification(isDraft: boolean): void {
    const message = isDraft
      ? 'Behavior Draft has been saved successfully'
      : this.getSuccessMessage();
    this.commonService.notify('success', message, '', '450px');
  }

  private getSuccessMessage(): string {
    return this.caseParams.mode === InsightMode.EDIT_BEHAVIOR ||
      this.caseParams.mode === InsightMode.VIEW_BEHAVIOR
      ? 'Behavior has been updated successfully'
      : 'Behavior has been created successfully';
  }

  private handleError(error: Error): void {
    this.form.get('draft')?.setValue(true);
    this.loading = false;
    this.cd.detectChanges();
    this.commonService.notify('error', error.message);
  }

  closeDrawer(
    isRecordChange = false,
    result: BehaviorModel | null = null
  ): void {
    if (this.isContinueProcessing) {
      const behaviorResult: CaseBehaviour = {
        id: result?.id || 0,
        behaviorCategoryId: result?.behaviorCategoryId || 0,
        behaviorCategoryName:
          this.behaviorCategoryList.find(
            item => item.id === result?.behaviorCategoryId
          )?.name || '',
        behaviorCategoryTypes:
          this.behaviorDetail?.caseBehaviorTypes.map(item => {
            return {
              behaviorTypeId: item.behaviorTypeId,
              behaviorTypeName: item.behaviorTypeName,
              id: 0,
              caseBehaviorId: result?.id || 0,
            };
          }) || [],
        behaviorFunction: [],
        behaviorAssessmentGraph: [],
        statusId: 1,
        resourceAddedDate: new Date().toISOString(),
        isCorrectionNeeded: false,
        modifiedDate: new Date().toISOString(),
        dataEnteredDate: new Date().toISOString(),
        operationalDefinition: result?.operationalDefinition || '',
        appliedStrategyCount: 0,
      };
      this.openAssessmentFbaDrawer(behaviorResult, true);
      return;
    }
    const drawer = this.drawerService.getDrawerRef(DRAWER_IDS.DEFINE_BEHAVIOR);
    drawer?.close({
      isRecordChange: isRecordChange,
      isContinueProcessing: this.isContinueProcessing,
      result: result,
    });
  }
  openAssessmentFbaDrawer(behavior: CaseBehaviour, isDetailPage = false): void {
    this.drawerService
      .openDrawer(
        FbaAssessmentComponent,
        this.drawerFBATitleTpl as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          caseDetail: this.caseParams.case,
          behavior: behavior,
          chartImage: null,
          mode: isDetailPage ? InsightMode.EDIT : InsightMode.VIEW,
        },
        DRAWER_IDS.FBA
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.drawerService.closeDrawer(DRAWER_IDS.DEFINE_BEHAVIOR);
      });
  }

  closeAssessmentFbaDrawer(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.FBA);
  }

  control(ctrl: string): FormControl {
    return this.form.get(ctrl) as FormControl;
  }

  openExampleofGoals() {
    this.isGoalExampleModelVisible = true;
  }

  async print() {
    const factory = this.viewContainerRef.createComponent(
      PrintCaseBehaviorComponent
    );

    factory.instance.printData = null;
    const componentElement = factory.location.nativeElement;
    componentElement.style.display = 'none';
    this.isPrintLoading = true;
    await Promise.all([
      this.orgService
        .getOrganization(this.caseParams?.case?.student.organizationId)
        .toPromise(),
    ])
      .then(async ([orgRes]: [OrganizationModel | undefined]) => {
        let base64Image = '';
        if (orgRes?.logoUrl) {
          base64Image = await this.commonService.getBase64ImageFromUrl(
            orgRes.logoUrl
          );
        } else {
          base64Image = await this.commonService.getBase64ImageFromUrl(
            '/assets/images/logo.png'
          );
        }
        factory.instance.behaviorData = {
          caseParams: this.caseParams,
          logo: base64Image,
          orgDetail: orgRes,
          form: {
            behaviorCategoryId: _.get(
              _.find(this.behaviorCategoryList, {
                id: this.control('behaviorCategoryId').value,
              }),
              'name',
              null
            ),
            caseBehaviorTypeIds:
              this.control('caseBehaviorTypeIds').value.length > 0
                ? _.map(
                    _.filter(this.behaviourSubCategoryList, item =>
                      _.includes(
                        this.control('caseBehaviorTypeIds').value,
                        item.id
                      )
                    ),
                    'name'
                  )
                : [],
            operationalDefinition: this.control('operationalDefinition').value,
            behaviourFrequencyId: this.control('behaviourFrequencyId').value
              ? _.get(
                  _.find(this.behaviourFrequencyList, {
                    id: this.control('behaviourFrequencyId').value,
                  }),
                  'name',
                  null
                )
              : null,
            behaviourFrequencyCount: this.control('behaviourFrequencyCount')
              .value,
            isDurationRequired: this.control('isDurationRequired').value,
            duration: this.control('duration').value,
            behaviourIntensityId: this.control('behaviourIntensityId').value
              ? _.get(
                  _.find(this.behaviourIntensityList, {
                    id: this.control('behaviourIntensityId').value,
                  }),
                  'name',
                  null
                )
              : null,
            goals: this.control('goals').value,
            draft: [true],
            draftStep: [1],
            caseBehaviorAntecedentIds:
              this.control('caseBehaviorAntecedentIds').value.length > 0
                ? _.map(
                    _.filter(this.behaviourAntecedentList, item =>
                      _.includes(
                        this.control('caseBehaviorAntecedentIds').value,
                        item.id
                      )
                    ),
                    'name'
                  )
                : [],
            caseBehaviorEnvironmentIds:
              this.control('caseBehaviorEnvironmentIds').value.length > 0
                ? _.map(
                    _.filter(this.behaviourEnvironmentList, item =>
                      _.includes(
                        this.control('caseBehaviorEnvironmentIds').value,
                        item.id
                      )
                    ),
                    'name'
                  )
                : [],
            caseBehaviorConsequenceIds:
              this.control('caseBehaviorConsequenceIds').value.length > 0
                ? _.map(
                    _.filter(this.behaviorConsequenceList, item =>
                      _.includes(
                        this.control('caseBehaviorConsequenceIds').value,
                        item.id
                      )
                    ),
                    'name'
                  )
                : [],
            caseBehaviorSettingEventIds:
              this.control('caseBehaviorSettingEventIds').value.length > 0
                ? _.map(
                    _.filter(this.behaviourSettingEventList, item =>
                      _.includes(
                        this.control('caseBehaviorSettingEventIds').value,
                        item.id
                      )
                    ),
                    'name'
                  )
                : [],
            caseBehaviorTypeDescriptions:
              this.control('caseBehaviorTypeDescriptions').value.length > 0
                ? _.map(
                    this.control('caseBehaviorTypeDescriptions').value,
                    'description'
                  )
                : [],
            caseBehaviorConsequenceDescription:
              this.control('caseBehaviorConsequenceDescription').value.length >
              0
                ? _.map(
                    this.control('caseBehaviorConsequenceDescription').value,
                    'description'
                  )
                : [],
            caseBehaviorAntecedentDescription:
              this.control('caseBehaviorAntecedentDescription').value.length > 0
                ? _.map(
                    this.control('caseBehaviorAntecedentDescription').value,
                    'description'
                  )
                : [],
            caseBehaviorCustomQuestionAnswer: _.chain(
              this.control('caseBehaviorCustomQuestionAnswer').value
            )
              .map(item => {
                const title = this.getquestionTitleById(item.customQuestionId);
                return title === '' ? null : { ...item, customQuestion: title };
              })
              .filter(Boolean)
              .value(),
          },
        };

        this.cd.detectChanges();
        this.waitForInitialization(factory).then(async () => {
          await factory.instance.printPDF();
          factory.destroy();
          this.isPrintLoading = false;
          this.cd.markForCheck();
        });
      })
      .catch(e => console.error(e))
      .finally(() => {
        this.isPrintLoading = false;
        this.cd.markForCheck();
      });
  }

  get intensityName() {
    return this.behaviourIntensityList.find(
      q => q.id === this.form.controls['behaviourIntensityId'].value
    )?.name;
  }

  get isWarningModalVisible(): boolean {
    return (
      this.isVisible &&
      (this.intensityName === 'Moderate' || this.intensityName === 'Major')
    );
  }

  getquestionTitleById(id: number) {
    return (
      this.customQuestionList.find(
        q =>
          q.id === id &&
          q.questionForReportTypeId !== CustomQuestionReportType.BIP
      )?.customQuestion ?? ''
    );
  }

  getCategoryNameById(id: number) {
    return this.behaviorCategoryList.find(q => q.id === id)?.name ?? '';
  }

  getFrequencyNameById(id: number | null): string {
    if (!id) return '';
    const frequency = this.behaviourFrequencyList.find(f => f.id === id);
    return frequency ? this.getBehaviorFrequencyLabel(frequency.name) : '';
  }

  getIntensityNameById(id: number | null): string {
    if (!id) return '';
    return this.behaviourIntensityList.find(i => i.id === id)?.name ?? '';
  }

  getEnvironmentNameById(id: number): string {
    return this.behaviourEnvironmentList.find(e => e.id === id)?.name ?? '';
  }

  getSettingEventNameById(id: number): string {
    return this.behaviourSettingEventList.find(s => s.id === id)?.name ?? '';
  }

  waitForInitialization(factory: ComponentRef<PrintCaseBehaviorComponent>) {
    return new Promise<void>(resolve => {
      const interval = setInterval(() => {
        if (factory.instance.initialized) {
          clearInterval(interval);
          resolve();
        }
      }, 10);
    });
  }

  openPdfModal() {
    this.isPdfModalVisible = true;
  }

  closePdfModal() {
    this.isPdfModalVisible = false;
  }
}
